import { motion } from 'framer-motion';
import type { CharacteristicAverage } from '../../lib/coach-dashboard';

interface CoachCharacteristicsOverviewProps {
  data: CharacteristicAverage[];
  locale?: string;
}

const categoryColors: Record<string, { bar: string; bg: string; label: string }> = {
  being: { bar: 'bg-teal-50', bg: 'bg-teal-10', label: 'Being' },
  doing: { bar: 'bg-orange-40', bg: 'bg-orange-10', label: 'Doing' },
  having: { bar: 'bg-success-40', bg: 'bg-success-10', label: 'Having' },
};

export function CoachCharacteristicsOverview({ data, locale }: CoachCharacteristicsOverviewProps) {
  const tr = locale === 'tr';
  if (data.length === 0) {
    return (
      <div className="freud-card-elevated p-6">
        <h3 className="text-text-xl text-gray-80 mb-4">{tr ? 'Karakteristik Ortalamalar' : 'Characteristic Averages'}</h3>
        <p className="text-text-sm text-gray-40 text-center py-8">
          {tr ? 'Henuz degerlendirme verisi yok' : 'No assessment data available yet'}
        </p>
      </div>
    );
  }

  const grouped = {
    being: data.filter(d => d.category === 'being'),
    doing: data.filter(d => d.category === 'doing'),
    having: data.filter(d => d.category === 'having'),
  };

  return (
    <div className="freud-card-elevated p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-text-xl text-gray-80">{tr ? 'Karakteristik Ortalamalar' : 'Characteristic Averages'}</h3>
        <div className="flex items-center gap-3">
          {Object.entries(categoryColors).map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-full ${val.bar}`} />
              <span className="text-text-xs text-gray-40">{val.label}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-text-sm text-gray-40 mb-5">
        Average scores across all respondents
      </p>

      <div className="space-y-6">
        {(Object.entries(grouped) as [string, CharacteristicAverage[]][]).map(([category, items]) => (
          <div key={category}>
            <div className="space-y-2.5">
              {items.map((item, index) => {
                const colors = categoryColors[category];
                return (
                  <div key={item.characteristicId} className="flex items-center gap-3">
                    <span className="text-text-sm text-gray-60 w-[130px] flex-shrink-0 truncate">
                      {item.name}
                    </span>
                    <div className={`flex-1 h-7 ${colors.bg} rounded-full overflow-hidden`}>
                      <motion.div
                        className={`h-full ${colors.bar} rounded-full flex items-center justify-end pr-2.5`}
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.max(item.average, 3)}%` }}
                        transition={{ duration: 0.6, delay: index * 0.05 }}
                      >
                        {item.average >= 15 && (
                          <span className="text-text-xs text-white font-semibold">
                            {item.average}%
                          </span>
                        )}
                      </motion.div>
                    </div>
                    {item.average < 15 && (
                      <span className="text-text-xs text-gray-50 font-medium w-10 text-right">
                        {item.average}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
